import { useEffect, useState } from "react";
import { Star, Send, CheckCircle2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../services/api";

/**
 * Sprint 74 — In-page NPS prompt.
 *
 * Shown on the delivered-order details page. One-tap 0-10 score grid +
 * optional free-text comment. Submits to POST /api/feedback. Re-mounts
 * read /api/feedback/me/{tid} so the form pre-fills if the user already
 * submitted (lets them edit). Auto-collapses after submit.
 *
 * Props:
 *   trackingId: string (required)
 *   onSubmitted?: (nps_category) => void
 *   compact?: boolean — denser layout for the order details modal
 */
export default function NPSPrompt({ trackingId, onSubmitted, compact = false }) {
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!trackingId) return undefined;
    (async () => {
      try {
        const { data } = await api.get(`/feedback/me/${encodeURIComponent(trackingId)}`);
        if (!alive) return;
        if (data?.feedback) {
          setScore(data.feedback.nps_score);
          setComment(data.feedback.comment || "");
          setSubmitted(true);
        }
      } catch {
        /* anonymous / fresh form */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [trackingId]);

  const submit = async () => {
    if (score === null) {
      toast.error("Pick a score from 0 to 10");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/feedback", {
        tracking_id: trackingId,
        nps_score: score,
        comment: comment.trim() || null,
        source: "delivered_page",
      });
      setSubmitted(true);
      toast.success("Thanks for the feedback!");
      onSubmitted?.(data?.nps_category);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (dismissed) return null;
  if (loading) return null;

  if (submitted) {
    return (
      <div
        className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3"
        data-testid="nps-prompt-thanks"
      >
        <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-emerald-900">
            Thanks! You rated this delivery {score}/10.
          </p>
          {comment && <p className="text-xs text-emerald-700 mt-0.5 italic">"{comment}"</p>}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white border border-slate-200 rounded-2xl ${compact ? "p-4" : "p-5"} shadow-sm`}
      data-testid="nps-prompt"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center">
              <Star className="h-4 w-4 text-amber-500" />
            </div>
            <h3 className="font-semibold text-slate-900 text-sm sm:text-base">
              How was this delivery?
            </h3>
          </div>
          <p className="text-xs text-slate-500 mt-1 ml-10">
            How likely are you to recommend PickNGo to a friend?
          </p>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-slate-400 hover:text-slate-600 p-1"
          aria-label="Dismiss"
          data-testid="nps-prompt-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-11 gap-1 sm:gap-1.5 mt-2" data-testid="nps-score-grid">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
          const selected = score === n;
          const bucket = n >= 9 ? "promoter" : n >= 7 ? "passive" : "detractor";
          const colors = {
            promoter: selected
              ? "bg-emerald-500 text-white border-emerald-500"
              : "bg-white hover:bg-emerald-50 border-slate-200 text-slate-700",
            passive: selected
              ? "bg-amber-400 text-white border-amber-400"
              : "bg-white hover:bg-amber-50 border-slate-200 text-slate-700",
            detractor: selected
              ? "bg-rose-500 text-white border-rose-500"
              : "bg-white hover:bg-rose-50 border-slate-200 text-slate-700",
          };
          return (
            <button
              key={n}
              type="button"
              onClick={() => setScore(n)}
              className={`min-h-[44px] text-sm font-semibold rounded-lg border ${colors[bucket]} transition`}
              data-testid={`nps-score-${n}`}
              aria-label={`Score ${n} out of 10`}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-slate-400 mt-1.5 px-1">
        <span>Not at all likely</span>
        <span>Extremely likely</span>
      </div>

      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Anything specific you'd like to share? (optional)"
        maxLength={2000}
        rows={2}
        className="mt-3 w-full text-sm rounded-xl border border-slate-200 p-2.5 focus:outline-none focus:ring-2 focus:ring-orange-200"
        data-testid="nps-comment"
      />

      <button
        onClick={submit}
        disabled={submitting || score === null}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm disabled:opacity-50 min-h-[44px]"
        data-testid="nps-submit"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        Submit feedback
      </button>
    </div>
  );
}
